import React, { useEffect, useState } from 'react';
import { X, Search, Loader2, MessageCircle, AlertTriangle } from 'lucide-react';
import {
  whatsappInboxService, WhatsAppConversation, WhatsAppLeadSearchResult,
} from '@/services/integrations/whatsappInboxService';
import useDebounce from '@/hooks/useDebounce';
import TemplatePicker from './TemplatePicker';
import { TemplateSendPayload } from './TemplateVariableForm';

interface StartConversationModalProps {
  onClose: () => void;
  /** Called with the resulting conversation — either a brand-new one after a
   * successful first send, or an existing one the search step resolved to. */
  onResolved: (conversation: WhatsAppConversation) => void;
  /** Skip the lead-search step and go straight to the template picker —
   * used when the caller already knows which lead (Lead modal, deep link). */
  lead?: { uuid: string; name?: string | null };
}

/**
 * "New conversation" entry point (scope.txt has no equivalent mockup — every
 * flow the CRM shipped with assumed a whatsapp_conversations row already
 * existed, created by an inbound webhook or a journey enrollment; this is
 * the one path that creates it from the CRM side instead).
 *
 * Two steps, skippable to the second when the caller already has a lead:
 *   1. search leads by name/phone — picking one that already has a
 *      conversation resolves straight to it instead of trying to start a
 *      duplicate (search results say so; see whatsappInboxService.searchLeadsForWhatsApp)
 *   2. TemplatePicker, reused as-is — the 24h window can't be open for a lead
 *      that's never messaged in, so only a template send can ever start one.
 */
const StartConversationModal: React.FC<StartConversationModalProps> = ({ onClose, onResolved, lead }) => {
  const [selectedLead, setSelectedLead] = useState<{ uuid: string; name?: string | null } | null>(lead || null);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 350);
  const [results, setResults] = useState<WhatsAppLeadSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [resolvingExisting, setResolvingExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedLead) return;
    if (!debouncedQuery.trim()) { setResults([]); return; }
    setSearching(true);
    whatsappInboxService.searchLeadsForWhatsApp(debouncedQuery)
      .then(setResults)
      .finally(() => setSearching(false));
  }, [selectedLead, debouncedQuery]);

  const handlePickLead = async (r: WhatsAppLeadSearchResult) => {
    if (!r.conversation_uuid) {
      setSelectedLead({ uuid: r.uuid, name: r.name });
      return;
    }
    // Already has a conversation — open it rather than attempting a duplicate start.
    setResolvingExisting(true);
    const convRes = await whatsappInboxService.listConversations({ lead_uuid: r.uuid, page_size: 1 });
    setResolvingExisting(false);
    if (convRes.data[0]) { onResolved(convRes.data[0]); onClose(); }
    else setError('Could not open the existing conversation — try again.');
  };

  const handleSend = async (payload: TemplateSendPayload) => {
    if (!selectedLead) return;
    setError(null);
    const result = await whatsappInboxService.startConversation(selectedLead.uuid, payload);
    if (!result.ok) { setError(result.error || 'Failed to start conversation'); return; }
    const convRes = await whatsappInboxService.listConversations({ lead_uuid: selectedLead.uuid, page_size: 1 });
    if (convRes.data[0]) onResolved(convRes.data[0]);
    onClose();
  };

  if (selectedLead) {
    return (
      <TemplatePicker
        onClose={onClose}
        onSend={handleSend}
        title={`Start a conversation${selectedLead.name ? ` with ${selectedLead.name}` : ''}`}
        banner={error && (
          <div className="mx-5 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px]">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
          </div>
        )}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-[#1B2531]/40 flex justify-end z-30">
      <div className="w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-gray-200 flex items-start gap-3">
          <div className="flex-1">
            <div className="text-[15.5px] font-bold">New conversation</div>
            <div className="text-xs text-gray-500 mt-0.5">Search a lead by name or phone</div>
          </div>
          <button onClick={onClose} className="w-[30px] h-[30px] rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50">
            <X className="w-[15px] h-[15px]" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 h-[34px] border border-gray-200 rounded-lg px-2.5 bg-gray-50">
            <Search className="w-3.5 h-3.5 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name or phone…"
              className="flex-1 text-[12.5px] bg-transparent border-0 outline-none placeholder:text-gray-400"
            />
          </div>
        </div>

        {error && (
          <div className="mx-5 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[12px]">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {searching || resolvingExisting ? (
            <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brandColor mx-auto" /></div>
          ) : !query.trim() ? (
            <div className="text-center py-16 px-6 text-sm text-gray-400">Start typing a lead's name or phone number…</div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 px-6 text-sm text-gray-400">No leads match.</div>
          ) : (
            results.map((r) => (
              <button
                key={r.uuid}
                onClick={() => handlePickLead(r)}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 border-b border-gray-50"
              >
                <span className="w-9 h-9 rounded-full bg-[#eef5f0] flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-4 h-4 text-[#153d23]" />
                </span>
                <span className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900 truncate">{r.name || 'Unknown'}</div>
                  <div className="text-[11.5px] text-gray-500">{r.phone}</div>
                </span>
                {r.conversation_uuid ? (
                  <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
                    Existing chat
                  </span>
                ) : (
                  <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-[#eef5f0] text-[#153d23] flex-shrink-0">
                    New
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StartConversationModal;
